import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "hidden", "results", "toggle"]
  static values = {
    url: String,
    cascadeFrom: String,
    fieldKey: String,
    allowFreeText: { type: Boolean, default: false }
  }

  connect() {
    this.selectedIndex = -1
    this.abortController = null
    this.search = this.debounce(this.search.bind(this), 250)
    this._outsideClickHandler = this.handleOutsideClick.bind(this)
    document.addEventListener("click", this._outsideClickHandler)

    this.bindDependencyListener()
  }

  disconnect() {
    document.removeEventListener("click", this._outsideClickHandler)
    this.abortController?.abort()

    this.dependencies?.forEach(({ element, eventName }) => {
      element.removeEventListener(eventName, this._dependencyChangedHandler)
    })
  }

  inputChanged() {
    const currentValue = this.inputTarget.value.trim()
    const selectedLabel = this.inputTarget.dataset.selectedLabel || ""

    if (currentValue !== selectedLabel) {
      this.hiddenTarget.value = ""
    }

    this.search()
  }

  search(event = null) {
    const query = this.shouldRevealAllOptions(event) ? "" : this.inputTarget.value.trim()
    const dependencies = this.dependencyParams()

    if (this.hasCascadeFromValue &&
        (dependencies.length !== this.cascadeKeys().length || dependencies.some(({ value }) => !value))) {
      this.hideResults()
      return
    }

    this.abortController?.abort()
    this.abortController = new AbortController()

    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("q", query)

    dependencies.forEach(({ key, value }) => url.searchParams.set(key, value))

    fetch(url.toString(), {
      headers: { Accept: "application/json" },
      signal: this.abortController.signal
    })
        .then(response => response.json())
        .then(data => this.renderResults(data))
        .catch(error => {
          if (error.name !== "AbortError") console.error(error)
        })
  }

  renderResults(items) {
    if (!items.length) {
      this.hideResults()
      return
    }

    this.resultsTarget.innerHTML = items.map((item, index) => {
      const label = this.escapeHtml(String(item.label))
      const value = this.escapeHtml(String(item.value))
      const metaAttributes = this.metaAttributes(item.meta || {})

      return `
        <button
          type="button"
          class="dropdown-item"
          data-action="click->autocomplete#resultClicked"
          data-index="${index}"
          data-value="${value}"
          data-label="${label}"
          ${metaAttributes}
        >
          ${label}
        </button>
      `
    }).join("")

    this.resultsTarget.classList.add("show")
    this.toggleTarget?.classList.add("open")
    this.selectedIndex = -1
  }

  resultClicked(event) {
    this.select(event.currentTarget)
  }

  selectFromEvent(event) {
    const { value, label, meta = {} } = event.detail || {}
    if (value == null || label == null) return

    this.setSelection({ value, label, meta })
  }

  select(element) {
    this.setSelection({
      value: element.dataset.value,
      label: element.dataset.label,
      meta: this.extractMeta(element)
    })
  }

  setSelection({ value, label, meta = {} }) {
    this.inputTarget.value = label
    this.hiddenTarget.value = value
    this.inputTarget.dataset.selectedLabel = label

    this.dispatchChanged({ value, label, meta })

    this.hideResults()
  }

  commit() {
    setTimeout(() => {
      const typedValue = this.inputTarget.value.trim()

      if (typedValue === "") {
        if (this.hiddenTarget.value || this.inputTarget.dataset.selectedLabel) {
          this.clearSelection()
          this.dispatchChanged({ value: "", label: "", meta: {} })
        }
      } else if (!this.hiddenTarget.value) {
        if (this.allowFreeTextValue) {
          this.hiddenTarget.value = typedValue
          this.inputTarget.dataset.selectedLabel = typedValue

          this.dispatchChanged({
            value: typedValue,
            label: typedValue,
            meta: {}
          })
        } else {
          this.inputTarget.value = this.inputTarget.dataset.selectedLabel || ""
        }
      }

      this.hideResults()
    }, 150)
  }

  clearSelection() {
    this.hiddenTarget.value = ""
    this.inputTarget.value = ""
    this.inputTarget.dataset.selectedLabel = ""
  }

  toggle() {
    if (this.resultsTarget.classList.contains("show")) {
      this.hideResults()
    } else {
      this.search({ revealAll: true })
      this.inputTarget.focus()
    }
  }

  keydown(event) {
    const items = this.resultItems()
    if (!items.length) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1)
        this.highlight(items)
        break
      case "ArrowUp":
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
        this.highlight(items)
        break
      case "Enter":
        if (this.selectedIndex >= 0) {
          event.preventDefault()
          this.select(items[this.selectedIndex])
        }
        break
      case "Escape":
        this.hideResults()
        break
    }
  }

  hideResults() {
    this.resultsTarget.innerHTML = ""
    this.resultsTarget.classList.remove("show")
    this.toggleTarget?.classList.remove("open")
    this.selectedIndex = -1
  }

  resultItems() {
    return Array.from(this.resultsTarget.querySelectorAll(".dropdown-item"))
  }

  highlight(items) {
    items.forEach((item, index) => {
      item.classList.toggle("active", index === this.selectedIndex)
    })
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.hideResults()
    }
  }

  shouldRevealAllOptions(event) {
    if (!this.hiddenTarget.value) return false

    return event?.revealAll === true || event?.type === "click" || event?.type === "focus"
  }

  bindDependencyListener() {
    if (!this.hasCascadeFromValue) return

    const scope = this.element.closest(".nested-fields") || this.element.closest("form") || document
    this._dependencyChangedHandler = () => this.reset()
    this.dependencies = this.cascadeKeys().map(key => {
      const selector = `[data-autocomplete-field-key-value="${key}"]`
      const element = scope.querySelector(selector) || document.querySelector(selector)
      if (!element) return null

      const eventName = element.matches("input, select, textarea") ? "change" : "autocomplete:changed"
      element.addEventListener(eventName, this._dependencyChangedHandler)
      return { key, element, eventName }
    }).filter(Boolean)
  }

  cascadeKeys() {
    if (!this.hasCascadeFromValue) return []

    return this.cascadeFromValue.split(",").map(key => key.trim()).filter(Boolean)
  }

  dependencyParams() {
    return (this.dependencies || []).map(({ key, element }) => ({
      key,
      value: this.dependencyValue(element)
    }))
  }

  dependencyValue(element) {
    if (element.matches("input, select, textarea")) return element.value || null

    const hidden = element.querySelector('[data-autocomplete-target="hidden"]')
    const input = element.querySelector('[data-autocomplete-target="input"]')

    return hidden?.value || input?.value || null
  }

  reset() {
    const hadValue = this.hiddenTarget.value || this.inputTarget.value
    this.clearSelection()
    this.hideResults()
    if (hadValue) this.dispatchChanged({ value: "", label: "", meta: {} })
  }

  dispatchChanged(detail = {}) {
    this.element.dispatchEvent(new CustomEvent("autocomplete:changed", {
      bubbles: true,
      detail
    }))
  }

  metaAttributes(meta = {}) {
    return Object.entries(meta).map(([key, value]) => {
      const attrName = `data-meta-${this.dasherize(key)}`
      const attrValue = this.escapeHtml(String(value))
      return `${attrName}="${attrValue}"`
    }).join(" ")
  }

  extractMeta(element) {
    return Object.entries(element.dataset).reduce((result, [key, value]) => {
      if (!key.startsWith("meta")) return result

      const metaKey = key.charAt(4).toLowerCase() + key.slice(5)
      result[this.underscore(metaKey)] = this.castDatasetValue(value)
      return result
    }, {})
  }

  castDatasetValue(value) {
    if (value === "true") return true
    if (value === "false") return false
    if (value === "null") return null
    return value
  }

  dasherize(value) {
    return String(value).replace(/([a-z\d])([A-Z])/g, "$1-$2").replace(/_/g, "-").toLowerCase()
  }

  underscore(value) {
    return String(value)
        .replace(/([a-z\d])([A-Z])/g, "$1_$2")
        .replace(/-/g, "_")
        .toLowerCase()
  }

  debounce(fn, delay) {
    let timeout
    return (...args) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => fn(...args), delay)
    }
  }

  escapeHtml(string) {
    const div = document.createElement("div")
    div.textContent = string
    return div.innerHTML
  }
}
